import React, { useState, useEffect } from 'react';
import { sendEmail } from '../utils/emailService';
import { showSuccessAlert } from '../utils/alertService';

function EcommerceKarachiPakistanSection() {
  // Typewriter effect state
  const words = ['Karachi', 'Pakistan'];
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [currentText, setCurrentText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const fullWord = words[currentWordIndex];
    let timer;

    if (!isDeleting) {
      // Typing characters
      if (currentText.length < fullWord.length) {
        timer = setTimeout(() => {
          setCurrentText(fullWord.slice(0, currentText.length + 1));
        }, 120);
      } else {
        // Word completed, pause before deleting
        timer = setTimeout(() => {
          setIsDeleting(true);
        }, 1800);
      }
    } else {
      // Deleting characters
      if (currentText.length > 0) {
        timer = setTimeout(() => {
          setCurrentText(fullWord.slice(0, currentText.length - 1));
        }, 70);
      } else {
        // Deletion complete, switch to next word
        setIsDeleting(false);
        setCurrentWordIndex((prev) => (prev + 1) % words.length);
      }
    }

    return () => clearTimeout(timer);
  }, [currentText, isDeleting, currentWordIndex]);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    brand: '',
    platform: 'Shopify / Shopify Plus',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    await sendEmail({
      form_type: 'E-Commerce Karachi Project Inquiry',
      name: formData.name,
      from_name: formData.name,
      user_name: formData.name,
      email: formData.email,
      from_email: formData.email,
      user_email: formData.email,
      phone: formData.phone,
      user_phone: formData.phone,
      company: formData.brand,
      brand: formData.brand,
      service: `E-Commerce Store on ${formData.platform}`,
      service_interested: 'E-Commerce Store Development',
      message: `E-Commerce Consultation Request:
Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone}
Brand: ${formData.brand}
Target Platform: ${formData.platform}
Message: ${formData.message}`
    });

    setLoading(false);
    setIsSubmitted(true);
    showSuccessAlert(
      'Proposal Request Sent!',
      `Thank you, ${formData.name || 'valued client'}! Your E-Commerce proposal request for ${formData.brand || 'your brand'} has been sent successfully.`
    );
    setFormData({
      name: '',
      email: '',
      phone: '',
      brand: '',
      platform: 'Shopify / Shopify Plus',
      message: ''
    });
    setTimeout(() => {
      setIsSubmitted(false);
    }, 5000);
  };

  return (
    <section className="ecom-location-section">
      <div className="container">
        {/* Section Header with Retype Animation */}
        <div className="ecom-location-header">
          <div className="ecom-location-badge-row">
            <span className="ecom-location-badge">Top-Rated E-Commerce Agency</span>
            <div className="ecom-location-badge-line"></div>
          </div>

          <h2 className="ecom-location-title">
            ECommerce Development Company In{' '}
            <span className="typewriter-highlight text-red">
              {currentText}
              <span className="typewriter-cursor">|</span>
            </span>
          </h2>

          <p className="ecom-location-subtitle">
            Scale your digital commerce enterprise with Pakistan's leading store architects. From custom headless builds to omnichannel platform setups, we deliver results.
          </p>
        </div>

        {/* 2-Column Grid: Form (Left) and Image (Right) */}
        <div className="ecom-location-grid">
          {/* Left Column: Inquiry Form Card */}
          <div className="ecom-form-card">
            <div className="ecom-form-header">
              <div className="ecom-form-red-bar"></div>
              <div className="ecom-form-header-text">
                <h3 className="ecom-form-title">Request a Free Store Consultation</h3>
                <p className="ecom-form-desc">Get a comprehensive store audit, tech roadmap & quote.</p>
              </div>
            </div>

            {isSubmitted ? (
              <div className="ecom-form-success">
                <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="#c40a0a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <h4>Inquiry Received Successfully!</h4>
                <p>Our senior e-commerce consultants in Pakistan will contact you within 24 hours.</p>
              </div>
            ) : (
              <form className="ecom-inquiry-form" onSubmit={handleSubmit}>
                <div className="ecom-form-row">
                  <div className="ecom-input-group">
                    <input 
                      type="text" 
                      name="name" 
                      placeholder="Your name *" 
                      value={formData.name}
                      onChange={handleChange}
                      required 
                    />
                  </div>
                  <div className="ecom-input-group">
                    <input 
                      type="email" 
                      name="email" 
                      placeholder="Email address *" 
                      value={formData.email}
                      onChange={handleChange}
                      required 
                    />
                  </div>
                </div>

                <div className="ecom-form-row">
                  <div className="ecom-input-group">
                    <input 
                      type="tel" 
                      name="phone" 
                      placeholder="Phone number *" 
                      value={formData.phone}
                      onChange={handleChange}
                      required 
                    />
                  </div>
                  <div className="ecom-input-group">
                    <input 
                      type="text" 
                      name="brand" 
                      placeholder="Brand / Company name" 
                      value={formData.brand}
                      onChange={handleChange} 
                    />
                  </div>
                </div>

                <div className="ecom-input-group full-width">
                  <select 
                    name="platform" 
                    value={formData.platform}
                    onChange={handleChange}
                    className="ecom-select-input"
                  >
                    <option value="Shopify / Shopify Plus">Shopify / Shopify Plus</option>
                    <option value="Custom Next.js / Headless">Custom Next.js / Headless Architecture</option>
                    <option value="Magento / Adobe Commerce">Magento / Adobe Commerce</option>
                    <option value="WooCommerce">WooCommerce (WordPress)</option>
                    <option value="BigCommerce">BigCommerce</option>
                    <option value="Wix eCommerce">Wix eCommerce</option>
                  </select>
                </div>

                <div className="ecom-input-group full-width">
                  <textarea 
                    name="message" 
                    placeholder="Tell us about your e-commerce store requirements *" 
                    rows="4"
                    value={formData.message}
                    onChange={handleChange}
                    required
                  ></textarea>
                </div>

                <div className="ecom-submit-wrapper">
                  <button type="submit" className="btn-ecom-location-submit" disabled={loading}>
                    {loading ? 'Sending...' : 'Send Inquiry'}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Right Column: Visual Image Frame */}
          <div className="ecom-location-right">
            <div className="ecom-location-img-wrapper">
              <img 
                src="/assets/images/ecom_karachi_pakistan_showcase.jpg" 
                alt="ECommerce Development Company In Karachi Pakistan" 
                className="ecom-location-img"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default EcommerceKarachiPakistanSection;
